// src/components/AIMito/FinancialDashboard.tsx
import React from 'react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend, // <-- Import Legend from recharts
} from 'recharts';

interface FinancialDashboardProps {
  report: string; // Composite report containing multiple P&L and/or Balance Sheet sections
}

interface FinancialData {
  // Profit & Loss
  totalIncome?: number;
  grossProfit?: number;
  totalExpenses?: number;
  netOperatingIncome?: number;
  // Balance Sheet
  netIncome?: number;
  totalAssets?: number;
  totalLiabilities?: number;
  totalEquity?: number;
  // Ratios
  currentRatio?: number;
  debtToEquity?: number;
  returnOnEquity?: number;
}

/**
 * Helper to check if a numeric value is valid and non-zero.
 * Returns true only if `val` is defined, not NaN, and not 0.
 */
function isValidNumber(val: number | undefined): val is number {
  return val !== undefined && !isNaN(val) && val !== 0;
}

/**
 * Extracts the *last* substring between startMarker and endMarker.
 * If not found, returns an empty string.
 */
function extractLastSection(
  text: string,
  startMarker: string,
  endMarker: string,
): string {
  const lastStart = text.lastIndexOf(startMarker);
  if (lastStart === -1) {
    return ''; // No match
  }
  let endPos = text.indexOf(endMarker, lastStart);
  if (endPos === -1) {
    endPos = text.length; // If endMarker not found, go to end of string
  }
  return text.substring(lastStart, endPos);
}

/**
 * Extracts a numeric value (with optional decimal) from a "Label: $1,234.56" pattern.
 * Allows an optional '%' sign (e.g., "0.00%").
 */
function extractValueFromSection(
  section: string,
  label: string,
): number | undefined {
  const regex = new RegExp(`${label}\\s*:\\s*\\$?(-?[\\d,]+(?:\\.\\d+)?)%?`);
  const match = section.match(regex);
  if (!match) return undefined;
  return parseFloat(match[1].replace(/,/g, ''));
}

function parseFinancialReport(report: string): FinancialData {
  // 1) Grab the last Balance Sheet section
  const balanceSheetSection = extractLastSection(
    report,
    'Part 1: Report Metadata',
    'End of Report',
  );

  // 2) Grab the last Profit & Loss section (assuming it ends at "Recommendations")
  const profitAndLossSection = extractLastSection(
    report,
    'Financial Summary',
    'Recommendations',
  );

  // 3) Parse Balance Sheet data
  const totalAssets = extractValueFromSection(
    balanceSheetSection,
    'Total Assets',
  );
  const totalLiabilities = extractValueFromSection(
    balanceSheetSection,
    'Total Liabilities',
  );
  const totalEquity = extractValueFromSection(
    balanceSheetSection,
    'Total Equity',
  );

  // Net Income can appear in either section
  let netIncome = extractValueFromSection(balanceSheetSection, 'Net Income');
  if (netIncome === undefined) {
    netIncome = extractValueFromSection(profitAndLossSection, 'Net Income');
  }

  // Ratios (from the Balance Sheet "Part 3: Derived and Calculated Insights")
  const currentRatio = extractValueFromSection(
    balanceSheetSection,
    'Current Ratio',
  );
  const debtToEquity = extractValueFromSection(
    balanceSheetSection,
    'Debt-to-Equity Ratio',
  );
  const returnOnEquity = extractValueFromSection(
    balanceSheetSection,
    'Return on Equity (ROE)',
  );

  // 4) Parse Profit & Loss data
  const totalIncome = extractValueFromSection(
    profitAndLossSection,
    'Total Income',
  );
  const grossProfit = extractValueFromSection(
    profitAndLossSection,
    'Gross Profit',
  );
  const totalExpenses = extractValueFromSection(
    profitAndLossSection,
    'Total Expenses',
  );
  const netOperatingIncome = extractValueFromSection(
    profitAndLossSection,
    'Net Operating Income',
  );

  // 5) Return the combined data
  return {
    totalIncome,
    grossProfit,
    totalExpenses,
    netOperatingIncome,
    netIncome,
    totalAssets,
    totalLiabilities,
    totalEquity,
    currentRatio,
    debtToEquity,
    returnOnEquity,
  };
}

const FinancialDashboard: React.FC<FinancialDashboardProps> = ({ report }) => {
  // Parse out the final sets of data from the last P&L and last Balance Sheet
  const data = parseFinancialReport(report);

  // Prepare data for the bar chart (Income, Expenses, Net Income),
  // filtering out 0, undefined, or NaN.
  const barData = [
    { name: 'Income', value: data.totalIncome },
    { name: 'Expenses', value: data.totalExpenses },
    { name: 'Net Income', value: data.netIncome },
  ].filter((item) => isValidNumber(item.value));

  // Prepare data for the pie chart (Assets, Liabilities, Equity),
  // filtering out invalid or zero values.
  const pieData = [
    { name: 'Assets', value: data.totalAssets },
    { name: 'Liabilities', value: data.totalLiabilities },
    { name: 'Equity', value: data.totalEquity },
  ].filter((item) => isValidNumber(item.value));

  // We’ll only show the Net Income Trend chart if netIncome is valid and non-zero
  const netIncomeIsValid = isValidNumber(data.netIncome);

  // For Key Financial Ratios, filter out zero/invalid
  const ratioData = [
    { name: 'Current Ratio', value: data.currentRatio },
    { name: 'Debt-to-Equity', value: data.debtToEquity },
    { name: 'ROE', value: data.returnOnEquity },
  ].filter((item) => isValidNumber(item.value));

  // Set up the chart configurations for dynamic rendering
  const charts = [
    {
      title: 'Income vs Expenses',
      component: (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#8884d8" />
          </BarChart>
        </ResponsiveContainer>
      ),
      condition: barData.length > 0,
    },
    {
      title: 'Assets Composition',
      component: (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
            >
              {pieData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={['#0088FE', '#FFBB28', '#FF8042'][index % 3]}
                />
              ))}
            </Pie>
            <Tooltip />
            {/* Add a Legend specifically for the PieChart */}
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      ),
      condition: pieData.length > 0,
    },
    {
      title: 'Net Income Trend',
      component: (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={
              netIncomeIsValid
                ? [{ name: 'Current', value: data.netIncome }]
                : []
            }
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#82ca9d" />
          </LineChart>
        </ResponsiveContainer>
      ),
      condition: netIncomeIsValid,
    },
    {
      title: 'Key Financial Ratios',
      component: (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={ratioData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#82ca9d" />
          </BarChart>
        </ResponsiveContainer>
      ),
      condition: ratioData.length > 0,
    },
  ];

  return (
    <div
      className="financial-dashboard"
      style={{
        maxHeight: '80vh',
        overflowY: 'auto',
        padding: '1rem',
        background: 'white',
        borderRadius: '10px',
      }}
    >
      <div
        className="dashboard-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h2>Financial Overview</h2>
        {/* Add close button or other controls if needed */}
      </div>

      <div
        className="dashboard-content"
        style={{
          maxHeight: '65vh',
          overflowY: 'auto',
          paddingRight: '10px',
        }}
      >
        {charts.map(
          (chart, idx) =>
            chart.condition && (
              <div
                key={idx}
                className="chart-container"
                style={{ marginBottom: '2rem' }}
              >
                <h3>{chart.title}</h3>
                {chart.component}
              </div>
            ),
        )}
      </div>
    </div>
  );
};

export default FinancialDashboard;
